// components/common/MatterList.tsx
import React, { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { Flex } from 'antd';
import { IMatter } from '../../../interface';
import { IconOutlineButton, Spin, Tooltip } from '../../Ant';
import { MatterListScrollArea } from '../../../assets/styles/components/dashboard.styled';
import MailMatterCard from './mailMatterCard';
import NoData from '../noData';
import { Refresh, BriefcaseEmpty } from '../../icons';
import SkeletonCardList from '../../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';

interface MailMatterListProps {
  matters: IMatter[];
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onSelectMatter: (matter: IMatter, event: SyntheticEvent) => void;
  selectedMatterId?: string;
  'data-testid'?: string;
  className?: string;
  /**
   * Search row that scrolls away with the list. Supplying it swaps the plain scroll area for the
   * collapsing-header layout. Receives the list's refresh control so the row can place it
   * alongside its own actions instead of it being stacked against the field label.
   */
  header?: (refreshButton: ReactNode) => ReactNode;
  onRefresh?: () => void | Promise<unknown>;
}

const MailMatterList: React.FC<MailMatterListProps> = ({
  matters,
  isLoading,
  isFetching,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onSelectMatter,
  selectedMatterId,
  'data-testid': testId = 'matter-list',
  className,
  header,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && matters?.length > 0 && !isLoading) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, isLoading, matters?.length, fetchNextPage]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setIsRefreshing(false);
    }
  };

  const showSkeleton = isLoading || isRefreshing;

  const renderLoader = () => {
    if (isFetching) {
      return <Spin data-testid={`${testId}-small-spinner`} size="small" />;
    }
    return null;
  };

  const refreshButton = onRefresh ? (
    <Tooltip title={t('common:refresh')} arrow={false}>
      <IconOutlineButton
        aria-label={t('common:refresh')}
        icon={<Refresh className="fz-14" />}
        onClick={handleRefresh}
        className="topbar-cta"
        data-testid={`${testId}-refresh-button`}
      />
    </Tooltip>
  ) : null;

  const cards =
    !showSkeleton &&
    matters &&
    matters.length > 0 &&
    matters.map((matter: IMatter, index: number) => {
      return (
        <MailMatterCard
          key={matter?.id}
          matter={matter}
          onClick={onSelectMatter}
          isSelected={selectedMatterId === matter?.id}
          data-testid={`${testId}-card-${index}`}
        />
      );
    });

  const loadMoreTrigger = hasNextPage && (
    <div ref={ref} className="loading-indicator" data-testid={`${testId}-load-more-trigger`} />
  );

  const placeholder = showSkeleton ? (
    <SkeletonCardList rows={[1]} withSubcontentOnFirstRow withHeader={false} />
  ) : (
    renderLoader()
  );

  const emptyState = !showSkeleton && (!matters || matters.length === 0) && (
    <div className="flex-grow d-flex justify-center items-center" data-testid={`${testId}-no-data`}>
      <NoData
        header={t('matter:noMatters')}
        subHeader={t('common:tryChangingSearchCriteria')}
        icon={<BriefcaseEmpty />}
        data-testid={`${testId}-no-data-component`}
      />
    </div>
  );

  if (header) {
    return (
      <CollapsibleListLayout
        testId={testId}
        header={header(refreshButton)}
        contentClassName={className || ''}
        /* whatever sits above this list — the subject field or the suggestions panel — already
           leaves a gutter under itself, so the search row must not add a second one */
        ownsTopGutter={false}
        headerGap={10}
      >
        {cards}
        {loadMoreTrigger}
        {emptyState}
        {placeholder}
      </CollapsibleListLayout>
    );
  }

  return (
    <MatterListScrollArea data-testid={testId} className={className}>
      <Flex gap={10} vertical data-testid={`${testId}-container`}>
        {refreshButton && (
          <Flex justify="flex-end" className="w-full">
            {refreshButton}
          </Flex>
        )}
        {cards}
        {loadMoreTrigger}
        {placeholder}
      </Flex>
      {emptyState}
    </MatterListScrollArea>
  );
};

export default React.memo(MailMatterList);
